import { Component, OnDestroy, OnInit, inject, signal, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { BrandingService } from '@next/core-lib/branding';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { listAnimations } from '../../../../animations';
import { CustomerLocation, SiteState } from '../../../../shared/models/shared.model';
import { Contactperson } from '../../../models/contactperson.model';
import {
  clearSelectedContactperson,
  getLocationsForRelationWithCustomerRequest,
  searchCustomersforRelation,
  setRelationWithLocationRequest,
  setSelectedContactpersonId,
} from '../../../store/contactperson.actions';
import {
  hasLocationWithoutRelation,
  getCustomerLocationIdsWithRelation,
  getCustomerRelationsAllLocationIds,
  getCustomerRelationsLocations,
  getSelectedContactperson,
  getSelectedContactpersonId,
  selectLoadingCustomersForSearchbox,
  getAllCustomersforSearchbox,
} from '../../../store/contactperson.selectors';

@Component({
  selector: 'app-location-relations',
  templateUrl: './relations-location-detail.component.html',
  styleUrls: ['./relations-location-detail.component.scss'],
  animations: [listAnimations],
  standalone: false,
})
export class RelationsLocationDetailComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly brandingService = inject(BrandingService);

  isLoading$: Observable<boolean> = this.store.select(selectLoadingCustomersForSearchbox);
  customers$: Observable<{ id: number; name: string }[]> = this.store.select(
    getAllCustomersforSearchbox,
  );
  locations$: Observable<CustomerLocation[]> = this.store.select(getCustomerRelationsLocations);

  contactpersonName$: Observable<string> = this.store.pipe(
    select(getSelectedContactperson),
    map((cp: Contactperson) => (cp ? `${cp.firstName} ${cp.lastName}` : '')),
  );

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getUrl));

  locationWithNoRelation$: Observable<boolean> = this.store.pipe(
    select(hasLocationWithoutRelation),
    map(
      (locationsWithNoRelation: CustomerLocation[]) =>
        !!locationsWithNoRelation && locationsWithNoRelation.length === 0,
    ),
  );

  customerId: number;
  contactpersonId: number;
  private readonly selectedCustomerIdSignal = signal<number | null>(null);

  isAgrifirm: boolean = this.brandingService.isTenant('Agrifirm');

  private readonly selectedContactpersonIdSignal = toSignal(
    this.store.select(getSelectedContactpersonId),
    {
      initialValue: null as number | null,
    },
  );

  constructor() {
    effect(() => {
      const customerId = this.selectedCustomerIdSignal();
      const contactpersonId = this.selectedContactpersonIdSignal();
      if (customerId && contactpersonId) {
        this.store.dispatch(
          getLocationsForRelationWithCustomerRequest({
            customerId,
            contactpersonId,
          }),
        );
      }
    });
  }

  ngOnInit(): void {
    this.contactpersonId = parseInt(
      this.activatedRoute.snapshot.params['contactpersonId'] as string,
      10,
    );
    this.customerId = parseInt(this.activatedRoute.snapshot.params['customerId'] as string, 10);

    if (this.contactpersonId) {
      this.store.dispatch(setSelectedContactpersonId({ contactpersonId: this.contactpersonId }));
    } else {
      this.store.dispatch(clearSelectedContactperson());
    }

    if (isNaN(this.customerId)) {
      this.customerId = undefined;
      return;
    }
    this.selectCustomer({ id: this.customerId });
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearSelectedContactperson());
  }

  searchValueChanged(searchValue: string) {
    this.store.dispatch(searchCustomersforRelation({ searchtext: searchValue }));
  }

  selectCustomer(customer: unknown) {
    const customerId = (customer as { id: number }).id;
    this.selectedCustomerIdSignal.set(customerId);
    this.customerId = customerId;
  }

  clickRelationForLocation(event: MouseEvent, location: CustomerLocation) {
    if (event) {
      event.preventDefault();
    }
    this.setRelationForLocation(false, false, location);
  }

  toggleRelationForLocation(event: MatSlideToggleChange) {
    this.setRelationForLocation(true, event.checked);
  }

  private setRelationForLocation(
    toggleAll: boolean,
    checked: boolean,
    toggledLocation?: CustomerLocation,
  ) {
    const setRelations = (locationIds: Array<number>) => {
      this.store.pipe(select(getSelectedContactpersonId), take(1)).subscribe((contactpersonId) =>
        this.store.dispatch(
          setRelationWithLocationRequest({
            customerId: this.customerId,
            contactpersonId,
            locationIds,
          }),
        ),
      );
    };

    if (toggleAll) {
      this.store
        .pipe(select(getCustomerRelationsAllLocationIds), take(1))
        .subscribe((locationIds: number[]) => setRelations(checked ? locationIds : []));
    } else {
      this.store
        .pipe(select(getCustomerLocationIdsWithRelation), take(1))
        .subscribe((locationIds: number[]) => {
          const index = locationIds.findIndex((id) => id === toggledLocation.id);
          if (index !== -1) {
            locationIds = locationIds.filter((id) => id !== locationIds[index]);
          } else {
            locationIds = [...locationIds, toggledLocation.id];
          }
          setRelations(locationIds);
        });
    }
  }

  back = () => this.router.navigate(['../'], { relativeTo: this.activatedRoute });
}
